import React, {useCallback, useEffect, useState} from 'react';

import {EventCodeGenerationController} from 'src/modules/ai';
import {
    EventCodeSuggestionsList,
} from 'src/modules/event-create/header-block/event-code/suggestEventCode/views/event-code-suggestions-list';
import {EventChangeRequest} from 'src/openapi/models/EventChangeRequest';
import {sanitizeSnakeCase} from 'src/utils/sanitizeSnakeCase';

import {Autosuggest} from '@pb/kit-component-autosuggest-v2';
import {
    HorizontalPositionElement,
    PushNotification,
    VerticalPositionElement,
} from '@pb/kit-component-push-notification';

import {EventCreationMode, HeaderFields} from '../../../constants';
import {MAX_LENGTH_CODE} from '../../../validations/event-code-validation';

import {useEventCodeData} from './hooks/useEventCodeData';
import {useEventCodeMatch} from './hooks/useEventCodeMatch';
import {useEventCodeSuggestions} from './hooks/useEventCodeSuggestions';
import {SelectedEventCodeValidator} from './selected-event-code-validator';
import {StringFilterItem} from './typedefs';

import {listbox} from './suggest-event-code.module.styl';

type Props = {
    eventChangeRequest: EventChangeRequest,
    mode: EventCreationMode,
    hasValidationError: boolean,
    code: string,
    source: string,
    changeRequestId?: number | null,
    onChange: (value: string, name?: string) => void,
};

export const SuggestEventCode = (props: Props) => {
    const {
        eventChangeRequest,
        mode,
        hasValidationError,
        code,
        source,
        changeRequestId,
        onChange,
    } = props;

    const [selectedEventCode, setSelectedEventCode] = useState(code);
    const [shown, setShown] = useState(false);
    const {eventsInfo, eventsInfoMapped, changeRequestsMapped, getData} = useEventCodeData(mode);
    const {changeRequestItems, eventItems, summaryCaption} =
        useEventCodeSuggestions(selectedEventCode, eventsInfoMapped, changeRequestsMapped);
    const hasResults = changeRequestItems.length > 0 || eventItems.length > 0;
    const {checkMatchesEvent} = useEventCodeMatch({
        eventsInfo, eventsInfoMapped, changeRequestsMapped, code, source, changeRequestId, onChange});

    useEffect(() => {
        setSelectedEventCode(code);
    }, [code, mode]);

    const onInputChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        setSelectedEventCode(event.target.value);
    }, []);

    const onBlur = (event: React.FocusEvent<HTMLInputElement>) => {
        setShown(false);
        const eventCode = sanitizeSnakeCase(event.target.value).toUpperCase();
        onChange(eventCode, HeaderFields.eventCode);
    };

    const onFocus = () => {
        getData();
    };

    const onClear = useCallback(() => {
        setSelectedEventCode('');
        onChange('', HeaderFields.eventCode);
    }, [onChange]);

    const onItemSelect = (item: StringFilterItem) => {
        setSelectedEventCode(item.name);
        onChange(item.name, HeaderFields.eventCode);
        onChange(item.source, HeaderFields.eventSource);

        checkMatchesEvent(item.name, item.source);
    };

    const onEventCodeGenerate = (eventCode: string) => {
        onChange(eventCode, HeaderFields.eventCode);
    };

    return (
        <PushNotification anchorOrigin={{
            horizontal: HorizontalPositionElement.RIGHT,
            vertical: VerticalPositionElement.TOP,
        }}>
            <>
                <SelectedEventCodeValidator
                    selectedEventCode={selectedEventCode}
                    setSelectedEventCode={setSelectedEventCode}
                />
                <Autosuggest
                    name={HeaderFields.eventCode}
                    value={selectedEventCode}
                    placeholder="Event Code"
                    autoFocus={false}
                    autoComplete="off"
                    maxLength={MAX_LENGTH_CODE}
                    itemsForScroll={8}
                    disabled={mode === EventCreationMode.update}
                    color={hasValidationError ? 'ERROR' : 'DEFAULT'}
                    listboxProps={{className: listbox}}
                    shown={Boolean(shown && hasResults)}
                    setShown={setShown}
                    onChange={onInputChange}
                    onBlur={onBlur}
                    onClear={mode === EventCreationMode.create ? onClear : undefined}
                    onFocus={onFocus}
                    onItemSelect={onItemSelect}
                    endAdornment={mode === EventCreationMode.create ? (
                        <EventCodeGenerationController
                            eventChangeRequest={eventChangeRequest}
                            onEventCodeGenerate={onEventCodeGenerate}
                        />
                    ) : null}>
                    <EventCodeSuggestionsList
                        summaryCaption={summaryCaption}
                        changeRequestItems={changeRequestItems}
                        eventItems={eventItems}
                    />
                </Autosuggest>
            </>
        </PushNotification>
    );
};
